import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Box, Toolbar } from '@mui/material';
import { RootState } from 'client';

import { requestUserRegister } from '@atom/actions/userActions';
import Logo from '@atom/components/common/navigation/Logo';
import { Icon, IconButton, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import layoutStyles from '@atom/styles/layout';
import accountUtilities from '@atom/utilities/accountUtilities';
import history from '@atom/utilities/history';

import GoogleSocialLoginButton from './GoogleSocialLoginButton';
import LoginFooter from './LoginFooter';

import './login.css';

const navBarStyle = {
  height: layoutStyles.topNavHeight,
  backgroundColor: colors.neutral.white,
  boxShadow: '0 2px 4px 1px',
  color: colors.utility.boxShadow,
  position: 'fixed',
  top: 0,
};

const styles = {
  activeIcon: {
    fontSize: '24px',
    color: colors.neutral.white,
  },
  inactiveIcon: {
    fontSize: '24px',
    color: colors.neutral.gray,
  },
  passwordField: {
    paddingTop: '0.4rem',
  },
};

interface RegistrationInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
}

interface ErrorsState {
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
  firstName: boolean;
  lastName: boolean;
}

const Registration = () => {
  const dispatch = useDispatch();
  const loading = useSelector(
    (state: RootState) => state.loading.loadingUserRegistration,
  );

  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [input, setInput] = useState<RegistrationInput>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [errors, setErrors] = useState<ErrorsState>({
    email: false,
    confirmPassword: false,
    password: false,
    firstName: false,
    lastName: false,
  });

  const navigateToLogin = () => {
    history.push('/login');
  };

  const onChange = (property: keyof RegistrationInput, value: string) => {
    setInput(currentInput => ({
      ...currentInput,
      [property]: value,
    }));

    setErrors(currentErrors => ({
      ...currentErrors,
      [property]: false,
    }));
  };

  const validateValues = () => {
    const { email, firstName, lastName, password, confirmPassword } = input;

    const validEmail = accountUtilities.isValidEmail(email);
    const validFirstName = firstName;
    const validLastName = lastName;
    const validPassword = accountUtilities.isValidPassword(password);
    const validConfirmPassword =
      confirmPassword && password === confirmPassword;

    setErrors(currentErrors => ({
      ...currentErrors,
      email: !validEmail,
      firstName: !validFirstName,
      lastName: !validLastName,
      password: !validPassword,
      confirmPassword: !validConfirmPassword,
    }));

    return (
      validEmail &&
      validFirstName &&
      validLastName &&
      validPassword &&
      validConfirmPassword
    );
  };

  const submit = () => {
    const allValid = validateValues();

    if (allValid) {
      dispatch(
        requestUserRegister({
          firstName: input.firstName,
          lastName: input.lastName,
          email: input.email,
          password: input.password,
          phone: input.phone,
        }),
      );
    }
  };

  return (
    <>
      <AppBar style={navBarStyle}>
        <Toolbar>
          <div>
            <Logo />
          </div>
          <Box sx={{ flexGrow: 1 }} />
          <div styleName="nav-right-container">
            <div styleName="nav-right-text">Already have an account?</div>
            <div styleName="nav-right-login" onClick={navigateToLogin}>
              Sign In
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <div styleName="container">
        <div styleName="auth-background base" />
        <div styleName="heading-text">Let's get started</div>
        <div styleName="registration-icon-container">
          <div styleName="registration-icon active">
            <Icon className="material-icons" style={styles.activeIcon}>
              person
            </Icon>
          </div>
          <div styleName="registration-icon">
            <Icon className="material-icons" style={styles.inactiveIcon}>
              email
            </Icon>
          </div>
          <div styleName="registration-icon">
            <Icon className="material-icons" style={styles.inactiveIcon}>
              business
            </Icon>
          </div>
        </div>
        <div styleName="icon-horizontal-bar" />
        <div styleName="text-field-container">
          <div styleName="text-field-registration">
            <TextField
              variant="standard"
              value={input.firstName}
              label="First Name"
              disabled={loading}
              onChange={event => onChange('firstName', event.target.value)}
              helperText={errors.firstName ? 'Please enter a first name.' : ''}
              error={errors.firstName}
            />
          </div>
          <div styleName="text-field-registration">
            <TextField
              variant="standard"
              value={input.lastName}
              label="Last Name"
              disabled={loading}
              onChange={event => onChange('lastName', event.target.value)}
              helperText={errors.lastName ? 'Please enter a last name.' : ''}
              error={errors.lastName}
            />
          </div>
          <div styleName="text-field-registration">
            <TextField
              variant="standard"
              type="email"
              value={input.email}
              label="Email"
              disabled={loading}
              onChange={event => onChange('email', event.target.value)}
              helperText={
                errors.email ? 'Please enter a valid email address.' : ''
              }
              error={errors.email}
            />
          </div>
          <div styleName="text-field-registration">
            <TextField
              variant="standard"
              style={styles.passwordField}
              type={passwordVisible ? 'text' : 'password'}
              value={input.password}
              label="Password"
              disabled={loading}
              onChange={event => onChange('password', event.target.value)}
              error={errors.password}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() =>
                      setPasswordVisible(currentValue => !currentValue)
                    }
                    size="small"
                  >
                    <Icon>
                      {passwordVisible ? 'visibility_off' : 'remove_red_eye'}
                    </Icon>
                  </IconButton>
                ),
              }}
            />
          </div>
          {errors.password && (
            <div styleName="password-error">
              Please enter a password that is at least (12) characters long and
              includes at least one lowercase letter, uppercase letter, number,
              and special character (!@#$%^&*).
            </div>
          )}
          <div styleName="text-field-registration">
            <TextField
              variant="standard"
              style={styles.passwordField}
              type={passwordVisible ? 'text' : 'password'}
              value={input.confirmPassword}
              label="Confirm Password"
              disabled={loading}
              onChange={event =>
                onChange('confirmPassword', event.target.value)
              }
              helperText={
                errors.confirmPassword
                  ? 'Passwords do not match. Please confirm your password.'
                  : ''
              }
              error={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() =>
                      setPasswordVisible(currentValue => !currentValue)
                    }
                    size="small"
                  >
                    <Icon>
                      {passwordVisible ? 'visibility_off' : 'remove_red_eye'}
                    </Icon>
                  </IconButton>
                ),
              }}
            />
          </div>
          <div styleName="text-field-registration">
            <TextField
              variant="standard"
              value={input.phone}
              label="Phone Number (Optional)"
              disabled={loading}
              onChange={event => onChange('phone', event.target.value)}
            />
          </div>
        </div>
        <div styleName="generic-button-container">
          <div styleName="generic-button" onClick={submit}>
            sign up
          </div>
        </div>
        <div styleName="registration-social-login-container">
          <div styleName="login-decision-break">
            <span>or</span>
          </div>
          <div styleName="social-login-container">
            <GoogleSocialLoginButton text="sign up with google" />
          </div>
        </div>
        <LoginFooter />
      </div>
    </>
  );
};

export default Registration;
